/**
 * Ingredients Service for FracFocus Generator
 * Handles ingredient data management and chemical code comparison
 */

import { getThirdPartyChems } from './apiThirdPartyChems.ts';
import { getCustomers } from './apiCustomers.ts';

// Function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// API base URL
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Cache for vendor information
let vendorsCache = null;

// Cache for customer information
let customersCache = null;

/**
 * Find a customer ID by name
 * @param {string} customerName - The customer name to look up
 * @returns {Promise<number|null>} The customer ID or null if not found
 */
export const findCustomerIdByName = async (customerName) => {
  try {
    if (!customerName) {
      console.log('No customer name provided to findCustomerIdByName');
      return null;
    }

    // ---------------------------
    // Helpers
    // ---------------------------

    // Generic corp/industry words to ignore (tune as needed)
    const STOPWORDS = new Set([
      'and', 'the', 'of', 'usa', 'us',
      // corporate suffixes
      'llc', 'l.l.c', 'inc', 'incorporated', 'co', 'company',
      'corp', 'corporation', 'ltd', 'limited', 'lp', 'l.p', 'plc',
      'partners', 'holdings', 'management',
      // common industry generics
      'operating', 'operations', 'resources', 'exploration',
      'production', 'development',
      'energy', 'oil', 'gas', 'petroleum'
    ]);

    const stripDiacritics = (s) =>
      s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

    const normalizeBase = (s) => {
      if (!s) return '';
      let out = stripDiacritics(String(s).toLowerCase());
      out = out.replace(/&/g, ' and ');               // unify "&" with "and"
      out = out.replace(/[^a-z0-9\s]/g, ' ');         // remove punctuation/symbols
      out = out.replace(/\s+/g, ' ').trim();
      return out;
    };

    // Tokens with stopwords removed
    const tokens = (s) => {
      const base = normalizeBase(s);
      if (!base) return [];
      return base.split(' ')
        .filter(t => t && !STOPWORDS.has(t));
    };

    // Phrase form for quick begins-with checks (keep industry words but drop corp suffix words at the tail)
    const normalizePhrase = (s) => {
      let base = normalizeBase(s);
      // drop trailing corporate words repeatedly at the end
      const TRAIL_CORP = /\b(?:llc|l\.l\.c|inc|incorporated|co|company|corp|corporation|ltd|limited|lp|l\.p|plc|partners|holdings|management|operating|operations)\b\.?$/;
      for (let i = 0; i < 3; i++) base = base.replace(TRAIL_CORP, '').replace(/\s+/g, ' ').trim();
      return base;
    };

    const weightedJaccard = (aTokens, bTokens, weight) => {
      const aSet = new Set(aTokens);
      const bSet = new Set(bTokens);
      const union = new Set([...aSet, ...bSet]);
      let interSum = 0, unionSum = 0;
      for (const t of union) {
        const w = weight(t);
        unionSum += w;
        if (aSet.has(t) && bSet.has(t)) interSum += w;
      }
      return unionSum > 0 ? interSum / unionSum : 0;
    };

    // ---------------------------
    // Load customers
    // ---------------------------
    console.log(`Finding customer ID for name: "${customerName}"`);
    if (!customersCache) {
      console.log('Fetching customers from API');
      customersCache = await getCustomers();
    }
    console.log(`Retrieved ${customersCache.length} customers from API`);

    // ---------------------------
    // Precompute token frequencies (for IDF-like weighting)
    // ---------------------------
    const allTokenFreq = new Map();
    const customerTokenCache = new Map(); // id -> tokens

    for (const c of customersCache) {
      const tks = tokens(c.name);
      customerTokenCache.set(c.id, tks);
      const seen = new Set();
      for (const t of tks) {
        if (seen.has(t)) continue;      // document frequency (count each token once per name)
        seen.add(t);
        allTokenFreq.set(t, (allTokenFreq.get(t) || 0) + 1);
      }
    }

    // Weight rare tokens higher. Smooth to avoid extremes.
    const weight = (t) => {
      const df = allTokenFreq.get(t) || 1;
      // smaller when common, larger when rare
      return 1 / Math.log2(2 + df);
    };

    // ---------------------------
    // Build search representations
    // ---------------------------
    const searchTokens = tokens(customerName);
    const searchPhrase = normalizePhrase(customerName);
    console.log(`Normalized search phrase: "${searchPhrase}"`);
    console.log(`Identity tokens: [${searchTokens.join(', ')}]`);

    if (searchTokens.length === 0 && !searchPhrase) {
      console.log('Search normalized to nothing after stopword filtering');
      return null;
    }

    // Identity tokens are your non-generic, non-corp tokens (already filtered).
    const mustHave = new Set(searchTokens);

    // ---------------------------
    // Score each candidate
    // ---------------------------
    let best = null;

    for (const c of customersCache) {
      const cPhrase = normalizePhrase(c.name);
      const cTokens = customerTokenCache.get(c.id) || tokens(c.name);

      // 1) hard preference: contains all identity tokens
      const hasAllIdentity = [...mustHave].every(t => cTokens.includes(t));

      // 2) core similarity: weighted Jaccard over identity tokens
      //    (limit both sides to identity space to reduce noise)
      const sim = weightedJaccard(searchTokens, cTokens, weight);

      // 3) small bonus: phrase begins-with alignment
      let prefixBonus = 0;
      if (cPhrase.startsWith(searchPhrase) || searchPhrase.startsWith(cPhrase)) {
        prefixBonus = 0.15; // tiny nudge, not decisive
      }

      // 4) small bonus: exact token set equality (very close names)
      const tokenSetEq = (a, b) => {
        if (a.length !== b.length) return false;
        const A = new Set(a), B = new Set(b);
        if (A.size !== B.size) return false;
        for (const t of A) if (!B.has(t)) return false;
        return true;
      };
      const exactTokenBonus = tokenSetEq(searchTokens, cTokens) ? 0.1 : 0;

      // Composite score: identity coverage dominates, then similarity + bonuses
      const score = (hasAllIdentity ? 2.0 : 0.0) + sim + prefixBonus + exactTokenBonus;

      console.log(
        `${c.name} (ID: ${c.id}) -> ` +
        `tokens=[${cTokens.join(' ')}], ` +
        `hasAllIdentity=${hasAllIdentity}, sim=${sim.toFixed(3)}, ` +
        `bonus=${(prefixBonus + exactTokenBonus).toFixed(2)}, score=${score.toFixed(3)}`
      );

      if (!best || score > best.score ||
          (score === best.score && c.name.length < best.c.name.length)) {
        // tie-breaker: slightly prefer shorter practical name if scores equal
        best = { c, score };
      }
    }

    // ---------------------------
    // Thresholds and return
    // ---------------------------
    if (best && best.score > 0) {
      console.log(`Selected: "${best.c.name}" (ID: ${best.c.id}) with score=${best.score.toFixed(3)}`);
      return best.c.id;
    }

    console.log(`No customer found for name: "${customerName}"`);
    return null;

  } catch (error) {
    console.error(`Error finding customer ID for name "${customerName}":`, error);
    return null;
  }
};

/**
 * Get all vendors from the vendors table
 * @returns {Promise<Array>} Array of vendor objects
 */
export const getVendors = async () => {
  try {
    // Return cached vendors if available
    if (vendorsCache) {
      return vendorsCache;
    }

    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/vendors`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch vendors: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Cache the vendors data
    vendorsCache = data;
    
    return data;
  } catch (error) {
    console.error('Error getting vendors:', error);
    // Return an empty array instead of throwing to prevent UI errors
    return [];
  }
};

/**
 * Get company name for a vendor ID
 * @param {number} vendorId - The vendor ID to look up
 * @returns {Promise<string>} The company name or empty string if not found
 */
export const getCompanyNameForVendorId = async (vendorId) => {
  try {
    if (!vendorId) {
      return '';
    }
    
    const vendors = await getVendors();
    const vendor = vendors.find(v => v.id === vendorId);
    
    return vendor ? vendor.company : '';
  } catch (error) {
    console.error(`Error finding company name for vendor ID ${vendorId}:`, error);
    return '';
  }
};

/**
 * Get a simplified list of ingredients for dropdowns and autocomplete
 * @returns {Promise<Array>} Array of ingredient objects with name and CAS number
 */
export const getIngredientsList = async () => {
  try {
    const ingredients = await getIngredients();
    
    // Return a simplified list with just the name and CAS number
    return ingredients.map(ingredient => ({
      name: ingredient.ingredientName,
      casNumber: ingredient.casNumber,
      code: ingredient.code
    }));
  } catch (error) {
    console.error('Error getting ingredients list:', error);
    // Return an empty array instead of throwing to prevent UI errors
    return [];
  }
};

/**
 * Get all ingredients from the ingredients table
 * @returns {Promise<Array>} Array of ingredient objects
 */
export const getIngredients = async () => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/ingredients`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch ingredients: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(data);
    
    // Group ingredients by code to ensure we don't lose any when multiple ingredients
    // share the same code
    const ingredientsByCode = {};
    
    // First, map all ingredients to our frontend structure
    const mappedIngredients = data.map(ingredient => ({
      id: ingredient.id,
      code: ingredient.code,
      tradeName: ingredient.product_name,
      supplier: ingredient.vendor_id, // We'll resolve this to company name later
      vendorId: ingredient.vendor_id, // Keep the original vendor_id
      purpose: ingredient.purpose,
      ingredientName: ingredient.ingredient_name,
      casNumber: ingredient.cas_number,
      percentInAdditive: ingredient.additive_fraction, // Stored as fraction (0-1) in database
      specificGravity: ingredient.specific_gravity,
      description: ingredient.description,
      comments: ingredient.comments || '',
      units: ingredient.units || 'gal',
      percentInHFJob: 0, // This will be calculated when processing chemicals
      massIngredient: 0 // This will be calculated when processing chemicals
    }));
    
    // Group ingredients by code
    mappedIngredients.forEach(ingredient => {
      if (!ingredientsByCode[ingredient.code]) {
        ingredientsByCode[ingredient.code] = [];
      }
      ingredientsByCode[ingredient.code].push(ingredient);
    });
    
    // Flatten the grouped ingredients back into an array
    // This ensures we keep all ingredients, even if multiple share the same code
    const allIngredients = Object.values(ingredientsByCode).flat();
    
    console.log(`Processed ${allIngredients.length} ingredients from ${Object.keys(ingredientsByCode).length} unique codes`);
    
    return allIngredients;
  } catch (error) {
    console.error('Error getting ingredients:', error);
    throw error;
  }
};

/**
 * Find an ingredient by its code
 * @param {string} code - The chemical code to look up
 * @param {number} customerId - Optional customer ID to check for third-party mappings
 * @returns {Promise<Object|null>} The ingredient object or null if not found
 */
export const findIngredientByCode = async (code, customerId = null) => {
  try {
    const ingredients = await getIngredients();
    
    // First try to find the ingredient directly
    const directMatch = ingredients.find(ingredient => ingredient.code === code);
    if (directMatch) {
      console.log(`Found direct match for code ${code}: ${directMatch.ingredientName}`);
      return directMatch;
    }
    
    // If no direct match and we have a customerId, check for third-party mappings
    if (customerId) {
      console.log(`No direct match found for code ${code}, checking third-party mappings with customer ID ${customerId}`);
      try {
        // Get third-party chemical mappings for this customer
        const mappings = await getThirdPartyChems(customerId);
        console.log(`Retrieved ${mappings.length} third-party mappings for customer ID ${customerId}`);
        
        // Look for all mappings for this code
        const matchedMappings = mappings.filter(m => m.chem_code === code);
        console.log(`Found ${matchedMappings.length} mappings for code ${code}`);
        
        if (matchedMappings && matchedMappings.length > 0) {
          // If we found mappings, look up all corresponding ingredients by ID
          const matchedIngredients = matchedMappings.map(mapping => {
            const ingredient = ingredients.find(ing => ing.id === mapping.ingredients_id);
            if (ingredient) {
              console.log(`Found ingredient with ID ${mapping.ingredients_id} for code ${code}: ${ingredient.ingredientName}`);
            } else {
              console.warn(`Could not find ingredient with ID ${mapping.ingredients_id} in ingredients list`);
            }
            return ingredient;
          }).filter(Boolean); // Filter out any null values
          
          // If we found at least one ingredient, return the first one
          // The rest will be handled when processing related ingredients
          if (matchedIngredients.length > 0) {
            console.log(`Returning first matched ingredient: ${matchedIngredients[0].ingredientName}`);
            return matchedIngredients[0];
          } else {
            console.warn(`No valid ingredients found for the matched mappings`);
          }
        } else {
          console.log(`No third-party mappings found for code ${code}`);
        }
      } catch (mappingError) {
        console.warn(`Error fetching third-party mappings for customer ${customerId}:`, mappingError);
        // Continue with normal processing if mappings can't be fetched
      }
    } else {
      console.log(`No customer ID provided, skipping third-party mapping lookup for code ${code}`);
    }
    
    // If we get here, no match was found
    return null;
  } catch (error) {
    console.error(`Error finding ingredient with code ${code}:`, error);
    throw error;
  }
};

/**
 * Process chemicals for a well by comparing codes to the ingredients table
 * @param {Object} wellData - The well data containing chemicals
 * @returns {Promise<Array>} Array of processed ingredient objects
 */
export const processChemicals = async (wellData) => {
  // Get customer ID from wellData if available
  let customerId = wellData?.customer_id;
  console.log(`Processing chemicals with customer ID: ${customerId}`);
  console.log('wellData:', wellData);
  
  // If customer_id is not provided but customer name is available, try to find the customer ID by name
  if (!customerId && wellData?.customer) {
    console.log(`No customer_id provided, but customer name "${wellData.customer}" is available. Trying to find customer ID by name.`);
    try {
      const foundCustomerId = await findCustomerIdByName(wellData.customer);
      if (foundCustomerId) {
        customerId = foundCustomerId;
        console.log(`Found customer ID ${customerId} for customer name "${wellData.customer}"`);
      } else {
        console.warn(`Could not find customer ID for customer name "${wellData.customer}"`);
      }
    } catch (error) {
      console.error(`Error finding customer ID for customer name "${wellData.customer}":`, error);
    }
  }
  
  // Log a warning if customer_id is still missing
  if (!customerId) {
    console.warn('No customer_id provided or found by name. Third-party chemical mappings will not be available.');
  }
  try {
    if (!wellData) {
      console.error('No well data provided to processChemicals');
      return [];
    }

    // Log the well data for debugging
    console.log('Processing chemicals for well data:', wellData);

    // Ensure we have chemicals data
    if (!wellData.chemicals || !wellData.chemicals.constructor || wellData.chemicals.constructor !== Object || Object.keys(wellData.chemicals).length === 0) {
      console.warn('No chemicals found in well data or invalid chemicals format');
      return [];
    }

    // Ensure totalClean is available and is a valid number
    let totalClean = parseFloat(wellData.totalClean) || parseFloat(wellData.clean) || 1; // Default to 1 to avoid division by zero
    
    // Make sure totalClean is a positive number
    totalClean = Math.max(totalClean, 1);
    
    if (!wellData.totalClean && wellData.clean) {
      wellData.totalClean = wellData.clean;
    }

    console.log(`Total clean volume: ${totalClean} gallons`);

    // Try to get ingredients from database, but don't fail if we can't
    let ingredients = [];
    try {
      ingredients = await getIngredients();
      console.log(`Found ${ingredients.length} ingredients in database`);
    } catch (error) {
      console.warn('Could not fetch ingredients from database, proceeding with empty list:', error);
    }
    
    const processedIngredients = [];
    let totalMass = 0;

    // Group chemicals by trade name to ensure proper organization
    const chemicalGroups = {};
    
    // First pass: group chemicals and calculate total mass
    for (const [code, amount] of Object.entries(wellData.chemicals)) {
      // Skip if amount is 0 or not present
      if (!amount) {
        console.log(`Skipping chemical ${code} with zero amount`);
        continue;
      }

      console.log(`Processing chemical ${code} with amount ${amount}`);

      // Find all ingredients in our ingredients table that match this code
      // First check for direct matches
      let matchedIngredients = ingredients.filter(ing => ing.code === code);
      
      // Exception for A1047: Always check third-party mappings for this code with strict matching
      if (code === 'A1047' && customerId) {
        console.log(`Code A1047 detected - forcing third-party mapping lookup for customer ID ${customerId}`);
        matchedIngredients = []; // Clear any direct matches to force third-party lookup
      }
      
      // If no direct matches and we have a customerId, check for third-party mappings
      if (matchedIngredients.length === 0 && customerId) {
        console.log(`Checking third-party mappings for code ${code} with customer ID ${customerId}`);
        try {
          // Get third-party chemical mappings for this customer
          const mappings = await getThirdPartyChems(customerId);
          console.log(`Retrieved ${mappings.length} third-party mappings for customer ID ${customerId}`);
          
          // Look for all mappings for this code
          const matchedMappings = mappings.filter(m => m.chem_code === code);
          console.log(`matchedMappings: ${matchedMappings}`)
          
          if (matchedMappings && matchedMappings.length > 0) {
            // If we found mappings, look up all corresponding ingredients by ID
            matchedIngredients = [];
            
            // Process each mapping and add the corresponding ingredient
            for (const mapping of matchedMappings) {
              console.log(`mappings: ${mapping.ingredients_id}`)
              const matchedIngredient = ingredients.find(ing => ing.id === mapping.ingredients_id);
              if (matchedIngredient) {
                matchedIngredients.push(matchedIngredient);
                console.log(`Found third-party mapping for code ${code} -> ingredient ID ${mapping.ingredients_id}`);
              }
            }
          }
        } catch (mappingError) {
          console.warn(`Error fetching third-party mappings for customer ${customerId}:`, mappingError);
          // Continue with normal processing if mappings can't be fetched
        }
      }
      
      // Calculate mass in pounds: volume (gal) * 8.345 (lbs/gal) * specific_gravity
      const amountValue = parseFloat(amount) || 0;
      
      
      // If we get here, either no mappings were found or there was an error processing them
      // Create a basic entry as a fallback
      if (matchedIngredients.length === 0) {
        console.log(`No matching ingredients found for code ${code}, creating basic entry`);
        
        // For fallback entries, we assume gallons as the default unit
        // Default specific gravity of 1.0 and 100% (1.0 as fraction) percentInAdditive
        const massIngredient = amountValue * 1.0 * 8.345 * 1; // percentInAdditive is 1 (100%)
        totalMass += massIngredient;
        
        // Create or add to group for empty trade name
        if (!chemicalGroups['']) {
          chemicalGroups[''] = [];
        }
        
        // If not found, create a basic entry with the code as the ingredient name
        chemicalGroups[''].push({
          tradeName: '',
          supplier: wellData.customer || '',
          vendorId: null,
          purpose: 'Fracturing',
          ingredientName: code, // Using code as ingredient name initially
          casNumber: '',
          percentInAdditive: 1, // 1 = 100% as fraction
          specificGravity: 1.0,
          massIngredient: massIngredient,
          units: 'gal',
          comments: 'Code not found in ingredients table',
          isNonMsds: false,
          code: code,
          volume: amountValue // Store original volume for reference
        });
        
        continue; // Skip to next chemical
      }
      
      console.log(`Found ${matchedIngredients.length} matching ingredients for code ${code}`);
      
      // Process each matching ingredient
      for (const matchedIngredient of matchedIngredients) {
        const specificGravity = parseFloat(matchedIngredient.specificGravity) || 1.0;
        
        // Check if units are "lb" or "lbs" (already in pounds)
        let massIngredient;
        let calculatedVolume;
        const unitsLower = (matchedIngredient.units || '').toLowerCase().trim();
        if (unitsLower === 'lb' || unitsLower === 'lbs') {
          // If units are already in pounds, don't do the conversion
          // percentInAdditive is stored as fraction (0-1), use directly
          massIngredient = amountValue * matchedIngredient.percentInAdditive;
          // Back-calculate volume from mass: volume = mass / (specific gravity * 8.345)
          calculatedVolume = specificGravity > 0 ? massIngredient / (specificGravity * 8.345) : 0;
        } else {
          // For gallons or any other unit, convert to pounds
          // percentInAdditive is stored as fraction (0-1), use directly
          massIngredient = amountValue * specificGravity * 8.345 * matchedIngredient.percentInAdditive;
          calculatedVolume = amountValue;
        }
        
        // Add to total mass
        totalMass += massIngredient;
        
        // Determine trade name for grouping
        const tradeName = matchedIngredient.tradeName || '';
        
        // Create or add to group
        if (!chemicalGroups[tradeName]) {
          chemicalGroups[tradeName] = [];
        }
        
        console.log(`Processing matching ingredient for code ${code}: ${matchedIngredient.ingredientName}`);
        
        // Get the ingredients that make up this chemical
        // If the ingredient has a percentInAdditive < 100, it means it's a component of a mixture
        // Otherwise, it's a single ingredient chemical (100%)
        
        // Create an entry for this ingredient
        const ingredientEntry = {
          tradeName: matchedIngredient.tradeName,
          supplier: matchedIngredient.supplier || wellData.customer || '',
          vendorId: matchedIngredient.vendorId || null,
          purpose: matchedIngredient.purpose || 'Fracturing',
          ingredientName: matchedIngredient.ingredientName,
          casNumber: matchedIngredient.casNumber,
          percentInAdditive: matchedIngredient.percentInAdditive || 1, // Default to 1 (100% as fraction)
          specificGravity: specificGravity,
          massIngredient: massIngredient,
          units: matchedIngredient.units || 'gal',
          comments: matchedIngredient.comments || '',
          isNonMsds: false,
          code: code,
          volume: calculatedVolume // Use calculated volume (either original or back-calculated from lbs)
        };
        
        chemicalGroups[tradeName].push(ingredientEntry);
      }
        
        // For each matched ingredient, look for other ingredients with the same trade name (components of this chemical)
        // Skip this for A1047 to prevent pulling in ingredients from other codes
        if (code !== 'A1047') {
          for (const matchedIng of matchedIngredients) {
            const relatedIngredients = ingredients.filter(ing => 
              ing.tradeName === matchedIng.tradeName && 
              ing.code !== code && 
              ing.ingredientName !== matchedIng.ingredientName
            );
            
            // Add each related ingredient as a separate entry
            relatedIngredients.forEach(relatedIng => {
              const relatedPercentInAdditive = relatedIng.percentInAdditive || 0;
              const specificGravity = parseFloat(matchedIng.specificGravity) || 1.0;
              
              // Check if units are "lb" or "lbs" (already in pounds)
              let massIngredient;
              let relatedVolume;
              const relatedUnitsLower = (relatedIng.units || '').toLowerCase().trim();
              if (relatedUnitsLower === 'lb' || relatedUnitsLower === 'lbs') {
                // If units are already in pounds, don't do the conversion
                // percentInAdditive is stored as fraction (0-1), use directly
                massIngredient = amountValue * relatedPercentInAdditive;
                // Back-calculate volume from mass: volume = mass / (specific gravity * 8.345)
                relatedVolume = specificGravity > 0 ? massIngredient / (specificGravity * 8.345) : 0;
              } else {
                // For gallons or any other unit, convert to pounds
                // percentInAdditive is stored as fraction (0-1), use directly
                massIngredient = amountValue * specificGravity * 8.345 * relatedPercentInAdditive;
                relatedVolume = amountValue * relatedPercentInAdditive;
              }
              const relatedMass = massIngredient;
              
              chemicalGroups[matchedIng.tradeName || ''].push({
                tradeName: relatedIng.tradeName,
                supplier: relatedIng.supplier || wellData.customer || '',
                vendorId: relatedIng.vendorId || null,
                purpose: relatedIng.purpose || 'Fracturing',
                ingredientName: relatedIng.ingredientName,
                casNumber: relatedIng.casNumber,
                percentInAdditive: relatedPercentInAdditive,
                specificGravity: parseFloat(relatedIng.specificGravity) || 1.0,
                massIngredient: relatedMass,
                units: relatedIng.units || 'gal',
                comments: relatedIng.comments || '',
                isNonMsds: false,
                code: relatedIng.code,
                volume: relatedVolume // Use calculated volume (either proportional or back-calculated from lbs)
              });
            });
          }
        }
    }
    
    // Second pass: calculate percentInHFJob based on mass
    if (totalMass > 0) {
      Object.values(chemicalGroups).forEach(group => {
        group.forEach(ingredient => {
          // Calculate percentInHFJob as mass of ingredient / total mass
          ingredient.percentInHFJob = parseFloat((ingredient.massIngredient / totalMass).toFixed(10));
        });
      });
    } else {
      console.warn('Total mass is zero, skipping percentage calculation');
    }
    
    // Flatten groups into final array, keeping items with the same trade name together
    Object.values(chemicalGroups).forEach(group => {
      processedIngredients.push(...group);
    });

    // Sort by trade name to ensure proper grouping in the UI
    processedIngredients.sort((a, b) => {
      if (a.tradeName === b.tradeName) {
        return 0;
      }
      return a.tradeName < b.tradeName ? -1 : 1;
    });

    // Check for a standalone water ingredient (not just water as a component of other ingredients)
    const standaloneWaterIngredient = processedIngredients.find(ing => 
      ing.casNumber === '7732-18-5' && ing.tradeName === 'Water' && ing.purpose === 'Carrier/Base Fluid'
    );
    
    // Always add water as the first ingredient
    console.log('Adding water as the first ingredient');
    
    // Calculate water mass
    // Water is always in gallons, so we always do the conversion
    const waterVolume = totalClean;
    const waterSpecificGravity = 1.0;
    const waterPercentInAdditive = 1; // 1 = 100% as fraction
    const waterMass = waterVolume * waterSpecificGravity * 8.345 * waterPercentInAdditive;
    
    // Add water mass to total mass for percentage calculation if it's not already there
    if (!standaloneWaterIngredient) {
      totalMass += waterMass;
    }
    
    // Create water ingredient
    const water = {
      tradeName: "Water",
      supplier: wellData.customer || '',
      vendorId: null,
      purpose: "Carrier/Base Fluid",
      ingredientName: "Water",
      casNumber: "7732-18-5",
      percentInAdditive: 1, // 1 = 100% as fraction
      specificGravity: 1.0,
      massIngredient: waterMass,
      units: "gal",
      comments: "",
      isNonMsds: false,
      code: "water-7732-18-5",
      volume: waterVolume
    };
    
    // Calculate percentInHFJob for water
    water.percentInHFJob = parseFloat((water.massIngredient / totalMass).toFixed(10));
    
    // Recalculate percentInHFJob for all ingredients based on new total mass
    processedIngredients.forEach(ingredient => {
      ingredient.percentInHFJob = parseFloat((ingredient.massIngredient / totalMass).toFixed(10));
    });
    
    // Remove any existing standalone water ingredient to avoid duplication
    if (standaloneWaterIngredient) {
      const waterIndex = processedIngredients.findIndex(ing => 
        ing.casNumber === '7732-18-5' && ing.tradeName === 'Water' && ing.purpose === 'Carrier/Base Fluid'
      );
      if (waterIndex !== -1) {
        processedIngredients.splice(waterIndex, 1);
      }
    }
    
    // Add water to the beginning of the ingredients list
    processedIngredients.unshift(water);
    
    console.log(`Processed ${processedIngredients.length} ingredients`);
    return processedIngredients;
  } catch (error) {
    console.error('Error processing chemicals:', error);
    throw error;
  }
};

/**
 * Add a new ingredient to the ingredients table
 * @param {Object} ingredient - The ingredient object to add
 * @returns {Promise<Object>} The added ingredient
 */
export const addIngredient = async (ingredient) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Convert frontend structure to backend structure
    const backendIngredient = {
      code: ingredient.code,
      vendor_id: ingredient.vendorId || ingredient.supplier, // Use vendorId if available, otherwise use supplier
      product_name: ingredient.tradeName,
      specific_gravity: ingredient.specificGravity || 1.0,
      description: ingredient.description || '',
      purpose: ingredient.purpose,
      ingredient_name: ingredient.ingredientName,
      cas_number: ingredient.casNumber,
      additive_fraction: ingredient.percentInAdditive, // Already a percentage, no need to convert
      comments: ingredient.comments || '',
      units: ingredient.units
    };
    
    const response = await fetch(`${API_BASE_URL}/api/ingredients`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(backendIngredient)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to add ingredient: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Map the response back to our frontend structure
    return {
      id: data.id,
      code: data.code,
      tradeName: data.product_name,
      supplier: data.vendor_id, // We'll resolve this to company name when displaying
      vendorId: data.vendor_id, // Keep the original vendor_id
      purpose: data.purpose,
      ingredientName: data.ingredient_name,
      casNumber: data.cas_number,
      percentInAdditive: data.additive_fraction,
      specificGravity: data.specific_gravity,
      description: data.description,
      comments: data.comments || '',
      units: data.units,
      percentInHFJob: 0,
      massIngredient: 0
    };
  } catch (error) {
    console.error('Error adding ingredient:', error);
    throw error;
  }
};

/**
 * Update an existing ingredient in the ingredients table
 * @param {string} code - The code of the ingredient to update
 * @param {Object} updatedIngredient - The updated ingredient data
 * @returns {Promise<Object>} The updated ingredient
 */
export const updateIngredient = async (code, updatedIngredient) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Convert frontend structure to backend structure
    const backendIngredient = {
      code: updatedIngredient.code,
      vendor_id: updatedIngredient.vendorId || updatedIngredient.supplier, // Use vendorId if available, otherwise use supplier
      product_name: updatedIngredient.tradeName,
      specific_gravity: updatedIngredient.specificGravity || 1.0,
      description: updatedIngredient.description || '',
      purpose: updatedIngredient.purpose,
      ingredient_name: updatedIngredient.ingredientName,
      cas_number: updatedIngredient.casNumber,
      additive_fraction: updatedIngredient.percentInAdditive,
      comments: updatedIngredient.comments || '',
      units: updatedIngredient.units
    };
    
    const response = await fetch(`${API_BASE_URL}/api/ingredients/${updatedIngredient.id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(backendIngredient)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update ingredient: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Map the response back to our frontend structure
    return {
      id: data.id,
      code: data.code,
      tradeName: data.product_name,
      supplier: data.vendor_id, // We'll resolve this to company name when displaying
      vendorId: data.vendor_id, // Keep the original vendor_id
      purpose: data.purpose,
      ingredientName: data.ingredient_name,
      casNumber: data.cas_number,
      percentInAdditive: data.additive_fraction,
      specificGravity: data.specific_gravity,
      description: data.description,
      comments: data.comments || '',
      units: data.units,
      percentInHFJob: updatedIngredient.percentInHFJob || 0,
      massIngredient: updatedIngredient.massIngredient || 0
    };
  } catch (error) {
    console.error(`Error updating ingredient with code ${code}:`, error);
    throw error;
  }
};

/**
 * Delete an ingredient from the ingredients table
 * @param {string} id - The ID of the ingredient to delete
 * @returns {Promise<boolean>} True if successful
 */
export const deleteIngredient = async (id) => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    const response = await fetch(`${API_BASE_URL}/api/ingredients/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete ingredient: ${response.statusText}`);
    }
    
    return true;
  } catch (error) {
    console.error(`Error deleting ingredient with ID ${id}:`, error);
    throw error;
  }
};

const IngredientsService = {
  getIngredients,
  getIngredientsList,
  findIngredientByCode,
  processChemicals,
  addIngredient,
  updateIngredient,
  deleteIngredient,
  getVendors,
  getCompanyNameForVendorId,
  findCustomerIdByName
};

export default IngredientsService;
